import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Gem, Sparkles, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { safeStorage } from "@/lib/safeStorage";

const SKIP_KEY = "mythos_welcome_spin_skipped";

const COLORS = ["#F59E0B", "#F43F5E", "#10B981", "#38BDF8", "#A855F7", "#F97316"];

// Welcome wheel for brand-new signups. Opens once per user; sets welcome_spin_used=true server-side.
export const WelcomeSpin = () => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [won, setWon] = useState(null);
  const [rotation, setRotation] = useState(0);
  const wheelRef = useRef(null);

  // Check availability once per login
  useEffect(() => {
    if (!user || user.is_admin) return;
    let cancelled = false;
    api.get("/me/welcome-spin").then((r) => {
      if (cancelled) return;
      setStatus(r.data);
      if (r.data.available && !safeStorage.getItem(SKIP_KEY)) {
        setOpen(true);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const spin = async () => {
    if (spinning || won != null) return;
    setSpinning(true);
    try {
      const { data } = await api.post("/me/welcome-spin");
      const wedges = data.wedges || [];
      const idx = wedges.indexOf(data.amount);
      const n = wedges.length;
      const degreesPerWedge = 360 / n;
      // Spin at least 6 full turns and land on the wedge centre.
      const target = 360 * 6 + (360 - (idx * degreesPerWedge + degreesPerWedge / 2));
      setRotation(target);
      // Reveal after the CSS transition completes.
      setTimeout(() => {
        setWon(data.amount);
        toast.success(`+${data.amount} gems added to your balance`);
        refresh();
      }, 4200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spin failed");
      setSpinning(false);
    }
  };

  const close = () => {
    setOpen(false);
    safeStorage.setItem(SKIP_KEY, "1");
  };

  if (!user || !status?.available) return null;
  const wedges = status.wedges || [10, 20, 30, 50, 75, 100];
  const n = wedges.length;
  const dpw = 360 / n;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent data-testid="welcome-spin-dialog" className="bg-[#0F1117] border-amber-500/30 max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-center flex items-center justify-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-400" /> Welcome spin
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-slate-400">
            One free spin — win between 10 and 100 bonus gems to start your first chat.
          </DialogDescription>
        </DialogHeader>
        <div className="relative mx-auto my-4 h-72 w-72">
          {/* Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-1 z-10 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[18px] border-l-transparent border-r-transparent border-t-amber-400 drop-shadow-[0_2px_6px_rgba(245,158,11,0.6)]" />
          {/* Wheel */}
          <div
            ref={wheelRef}
            data-testid="welcome-spin-wheel"
            className="h-full w-full rounded-full border-4 border-amber-500/40 shadow-[0_0_40px_rgba(245,158,11,0.3)] relative overflow-hidden"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.3, 1.0)" : "none",
              background: `conic-gradient(${wedges.map((_, i) => `${COLORS[i % COLORS.length]} ${i * dpw}deg ${(i + 1) * dpw}deg`).join(", ")})`,
            }}
          >
            {wedges.map((amount, i) => {
              const angle = i * dpw + dpw / 2;
              return (
                <div
                  key={i}
                  className="absolute left-1/2 top-1/2 origin-left -translate-y-1/2 pl-14"
                  style={{ transform: `rotate(${angle - 90}deg) translateX(0)` }}
                >
                  <div className="font-serif text-xl font-bold text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.4)]">
                    +{amount}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Center knob */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#0F1117] border-2 border-amber-500/60 flex items-center justify-center z-10">
            <Gem className="h-4 w-4 text-amber-300" />
          </div>
        </div>
        {won != null ? (
          <div data-testid="welcome-spin-won" className="text-center space-y-3">
            <div className="font-serif text-3xl font-bold text-amber-300">+{won} gems</div>
            <p className="text-sm text-slate-400">Credited to your balance. Go say hello to someone.</p>
            <Button onClick={close} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
              Start chatting
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button
              data-testid="welcome-spin-skip"
              onClick={close}
              variant="outline"
              className="flex-1 rounded-full border-white/15 text-slate-300"
              disabled={spinning}
            >
              Skip
            </Button>
            <Button
              data-testid="welcome-spin-go"
              onClick={spin}
              disabled={spinning}
              className="flex-1 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 hover:opacity-90 text-black font-semibold h-11"
            >
              {spinning ? "Spinning…" : "Spin the wheel"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
